import React from 'react';

import {browserHistory} from 'react-router';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {useBulkRecycleDocuments} from 'app/documents/api';
import StateButton from 'app/general/widgets/StateButton';
import {useDeleteKnowledgeBase} from 'app/kbs/api';
import {useDeleteProduct, useDeleteTemplate} from 'app/market/api';
import {useDeletePolicy} from 'app/policies/api';
import {deleteProcedure} from 'app/procedures/actions';
import {deleteProcess} from 'app/process/actions';
import {useDeleteQuiz} from 'app/quizzes/api';
import {useDeleteTag} from 'app/tags/api';
import {setDeletingDocumentFlag} from 'app/toasts/slice';
import {
  KB_DASHBOARD_URL,
  POLICIES_DASHBOARD_URL,
  PROCEDURES_HOME,
  PROCESS_DASHBOARD_URL,
  QUIZZES_HOME,
  TAGS_DASHBOARD_URL,
  TEMPLATES_DASHBOARD_URL,
} from 'app/urls';
import {getObjectName} from 'app/utils/fallbacks';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {DocumentSerializedType} from 'sweet-types';

const hardDeleteMap = {
  procedure: false,
  proceduregroup: false,
  policy: false,
  tag: true,
  product: true,
  knowledgebase: true,
  template: true,
};

type DeletableContentType =
  | 'procedure'
  | 'proceduregroup'
  | 'policy'
  | 'quiz'
  | 'tag'
  | 'product'
  | 'knowledgebase'
  | 'document'
  | 'template';

export interface DeletableItemType {
  name: string;
  content_type: DeletableContentType;
  url: string;
}

interface Props {
  show: boolean;
  object?: DeletableItemType;
  redirectContentType: DeletableContentType;
  documents?: Array<
    Pick<
      DocumentSerializedType,
      | 'document'
      | 'document_content_type'
      | 'url'
      | 'object_id'
      | 'can_edit'
      | 'can_delete'
    >
  >;
  onClose: () => void;
  onDelete?: (item: DeletableItemType, callback: () => unknown) => unknown;
}

export const DeleteDocumentModal = (props: Props): JSX.Element => {
  const {
    show,
    object = null,
    redirectContentType,
    documents = [],
    onClose,
    onDelete,
  } = props;
  const actions = useAsyncActions({
    deleteProcess,
    deleteProcedure,
    setDeletingDocumentFlag,
  });

  const [deleteTag] = useDeleteTag();
  const [deleteProduct] = useDeleteProduct();
  const [deleteKB] = useDeleteKnowledgeBase();
  const [deleteQuiz] = useDeleteQuiz();
  const [bulkRecycleDocuments] = useBulkRecycleDocuments();
  const [deletePolicy] = useDeletePolicy();
  const [deleteTemplate] = useDeleteTemplate();

  const doDelete = (): void | Promise<unknown> => {
    if (object == null) {
      bulkRecycleDocuments({
        documents: documents.map((doc) => ({
          object_id: doc.object_id,
          document_content_type: doc.document_content_type,
        })),
      });
      onClose();
      return;
    }

    const url = object?.url;
    const deleteFunction = {
      procedure: () => actions.deleteProcedure(url),
      proceduregroup: () => actions.deleteProcess(object),
      policy: () => deletePolicy(url).unwrap(),
      product: () => deleteProduct(url).unwrap(),
      tag: () => deleteTag(url).unwrap(),
      knowledgebase: () => deleteKB(url).unwrap(),
      quiz: () => deleteQuiz(url).unwrap(),
      template: () => deleteTemplate(url).unwrap(),
    }[object.content_type];

    actions.setDeletingDocumentFlag(true);

    return deleteFunction().then(
      () => {
        actions.setDeletingDocumentFlag(false);
        onClose();
        onDelete != null
          ? onDelete(object, () => redirectToDashboard(redirectContentType))
          : redirectToDashboard(redirectContentType);
        return object;
      },
      (e) => {
        actions.setDeletingDocumentFlag(false);
        return e;
      }
    );
  };

  const redirectToDashboard = (contentType: DeletableContentType) => {
    const url = {
      procedure: PROCEDURES_HOME,
      policy: POLICIES_DASHBOARD_URL,
      proceduregroup: PROCESS_DASHBOARD_URL,
      tag: TAGS_DASHBOARD_URL,
      knowledgebase: KB_DASHBOARD_URL,
      quiz: QUIZZES_HOME,
      template: TEMPLATES_DASHBOARD_URL,
    }[contentType];
    return browserHistory.push(url);
  };

  const name = <b>{getObjectName(object, object?.content_type, true)}</b>;
  const isHardDelete = object ? hardDeleteMap[object.content_type] : false;
  const actionPhrase = isHardDelete ? gt('Delete') : gt('Move to Recycle Bin');
  const modalText = isHardDelete
    ? gt('Are you sure you want to delete %(name)s', {name})
    : gt('Are you sure you want to move %(name)s to the Recycle Bin?', {name});

  return (
    <TabbedModal
      show={show}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'delete-object',
          name: actionPhrase,
          icon: 'far fa-trash',
          content: (
            <div>
              <p>
                {documents.length === 0
                  ? modalText
                  : ngt(
                      'Are you sure you want to move selected document to the Recycle Bin?',
                      'Are you sure you want to move selected documents to the Recycle Bin?',
                      documents.length
                    )}
              </p>
              <StyledModalFooter>
                <button
                  onClick={onClose}
                  className="btn btn-link text-secondary"
                  data-dismiss="modal"
                >
                  <i className="fa fa-times mr-1" />
                  {gt('Cancel')}
                </button>
                <StateButton
                  onClick={doDelete}
                  busyText={actionPhrase}
                  className="btn btn-danger"
                  data-dismiss="modal"
                >
                  {actionPhrase}
                </StateButton>
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
};
